import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { productApi } from "~/store/initializeStore";
import { type SalesMeetingRow } from "./useSalesMeetingsQuery";

export type PendingManualOrder = {
  id: string;
  productId: string;
  clientEmail: string;
  status: string | null;
  createdAt: string | null;
  amount: number | null;
  currency: string | null;
  name: string | null;
};

/**
 * Nieopłacone zamówienia manualne (przelew z koszyka) dla klientek z widocznej
 * strony spotkań — serwer zwraca wyłącznie te atrybuowalne do spotkań
 * ZALOGOWANEGO sprzedawcy (TRE-2006), więc dla ownera i innych ról lista jest
 * pusta i przycisk „Potwierdź wpłatę" się nie pojawia.
 */
export const usePendingManualOrdersQuery = ({
  meetings,
  enabled,
}: {
  meetings: SalesMeetingRow[];
  enabled: boolean;
}) => {
  const clientEmails = useMemo(
    () =>
      Array.from(
        new Set(
          meetings
            .map((meeting) => meeting.clientEmail?.trim().toLowerCase())
            .filter((email): email is string => Boolean(email)),
        ),
      ).slice(0, 100),
    [meetings],
  );

  const query = useQuery({
    queryKey: ["salesCrmPendingManualOrders", clientEmails],
    enabled: enabled && clientEmails.length > 0,
    retry: false,
    queryFn: async (): Promise<PendingManualOrder[]> => {
      const { data } = await productApi.post<{ orders: PendingManualOrder[] }>("/manual-payment/pending-for-seller", {
        clientEmails,
      });

      return data.orders ?? [];
    },
  });

  const pendingByEmail = useMemo(() => {
    const map = new Map<string, PendingManualOrder>();

    // Najnowsze zamówienie wygrywa — serwer zwraca je posortowane malejąco po
    // createdAt, więc pierwszy wpis dla adresu jest właściwy.
    for (const order of query.data ?? []) {
      const email = order.clientEmail.toLowerCase();
      if (!map.has(email)) {
        map.set(email, order);
      }
    }

    return map;
  }, [query.data]);

  return { ...query, pendingByEmail };
};
